//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { useInvalidateDatasets } from '@/services/xgtQueries';

/**
 * Freshness on arrival: invalidate the datasets query on every route change,
 * so the page a user lands on shows data at most one round trip old.  The
 * refetch is stale-while-revalidate — the destination renders instantly from
 * cache and silently updates — so navigation stays free of spinners.  This
 * also covers external writes the invalidate-on-write discipline can't see
 * (e.g. a load job started outside Mission Control finishing just before the
 * user switches tabs).  Schema views come along automatically since each
 * dataset's schema is embedded in the datasets payload.
 */
export const useNavigationInvalidation = () => {
  const location = useLocation();
  const invalidateDatasets = useInvalidateDatasets();
  // Skip the initial render: the datasets query fetches on its own when auth
  // settles, so invalidating then would just duplicate the first fetch.
  const lastPathname = useRef(location.pathname);

  useEffect(() => {
    if (lastPathname.current === location.pathname) return;
    lastPathname.current = location.pathname;
    void invalidateDatasets();
  }, [location.pathname, invalidateDatasets]);
};
